import { useCallback, useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../api/api';
import { queryKeys } from './queryKeys';

const fetchArtistsPage = async (page) => {
  const response = await api.get(`/artists?page=${page}`);
  return response.data;
};

export const useArtists = () => {
  const [extraData, setExtraData] = useState([]);
  const [pageN, setPageN] = useState(1);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const { data: queryData, isLoading: isInitialLoading } = useQuery({
    queryKey: queryKeys.artists.page(1),
    queryFn: () => fetchArtistsPage(1),
  });

  // When the initial page refetches after a mutation, reset extra pages.
  useEffect(() => {
    if (queryData?.data !== undefined) {
      setExtraData([]);
      setPageN(1);
      setHasMore(queryData?.hasMore ?? true);
    }
  }, [queryData?.data, queryData?.hasMore]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    const nextPage = pageN + 1;
    try {
      const response = await fetchArtistsPage(nextPage);
      const items = response?.data || [];
      setExtraData((prev) => [...prev, ...items]);
      setPageN(nextPage);
      setHasMore(response?.hasMore);
    } catch (error) {
      console.error('Failed to load more artists:', error);
      setHasMore(false);
    } finally {
      setIsLoadingMore(false);
    }
  }, [pageN, isLoadingMore, hasMore]);

  const finalData = useMemo(() => [...(queryData?.data ?? []), ...extraData], [queryData?.data, extraData]);

  return {
    data: finalData,
    isLoading: isInitialLoading || isLoadingMore,
    hasMore,
    loadMore,
  };
};